import { useCallback, useState } from "react";
import { FiEdit2, FiInfo } from "react-icons/fi";
import { useSelector } from "react-redux";
import Button from "components/Button";
import FormPasswordInput from "components/form/FormPasswordInput";
import ForgotPasswordResetPanel from "pages/settings/ForgotPasswordResetPanel";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import {
  validatePassword,
  validateConfirmPassword,
  validateForm as runFormValidation,
  required,
} from "lib/validation";
import { selectAuthUser } from "store/reducer";

const EMPTY_FORM = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export default function SettingsChangePasswordPage() {
  const user = useSelector(selectAuthUser);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const resetForm = useCallback(() => {
    setFormData(EMPTY_FORM);
    setErrors({});
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  }, []);

  const validateForm = useCallback(() => {
    const schema = {
      currentPassword: required("Current password"),
      newPassword: (v) => validatePassword(v),
      confirmPassword: (v) => validateConfirmPassword(v, formData.newPassword),
    };
    const newErrors = runFormValidation(formData, schema);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleCancelEdit = useCallback(() => {
    resetForm();
    setIsEditing(false);
  }, [resetForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing) return;

    if (!validateForm()) {
      await swtAlert.warning(
        "Check your password",
        "Please fill in all password fields correctly."
      );
      return;
    }

    setSaving(true);
    try {
      const resp = await api.post(API_ENDPOINTS.changePassword, {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      if (!isSuccessResponse(resp)) {
        await swtAlert.error(
          "Could not change password",
          resp?.message || "Please verify your current password and try again."
        );
        return;
      }
      resetForm();
      setIsEditing(false);
      await swtAlert.success(
        "Password updated",
        resp?.message || "Your password has been changed."
      );
    } catch (err) {
      await swtAlert.error(
        "Could not change password",
        err?.message || "Please try again or use forgot password."
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldDisabled = !isEditing;

  return (
    <div className="max-w-xl">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold text-text-900 md:text-2xl">Change password</h1>
        {isEditing ? null : (
          <button
            type="button"
            className="rounded-lg p-2 text-text-500 transition hover:bg-slate-100 hover:text-brand-900"
            onClick={() => setIsEditing(true)}
            aria-label="Edit password"
          >
            <FiEdit2 size={18} />
          </button>
        )}
      </div>
      <div className="mt-2 h-0.5 w-full bg-brand-900" aria-hidden />

      <div className="mt-6 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-text-700">
        <FiInfo className="mt-0.5 shrink-0 text-brand-900" aria-hidden />
        <p>
          {isEditing
            ? "Enter your current password, then choose a new strong password you do not use elsewhere."
            : "Your password is hidden for security. Click the edit icon to change it."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <FormPasswordInput
          label="Current password"
          name="currentPassword"
          placeholder="Current password"
          value={formData.currentPassword}
          onChange={handleChange}
          disabled={fieldDisabled}
          error={errors.currentPassword}
          autoComplete="current-password"
        />

        <FormPasswordInput
          label="New password"
          name="newPassword"
          placeholder="New password"
          value={formData.newPassword}
          onChange={handleChange}
          disabled={fieldDisabled}
          error={errors.newPassword}
          autoComplete="new-password"
        />
        {!errors.newPassword ? (
          <p className="-mt-3 text-xs text-text-500">
            Min. 8 characters with uppercase, lowercase, number and special character.
          </p>
        ) : null}

        <FormPasswordInput
          label="Confirm new password"
          name="confirmPassword"
          placeholder="Confirm new password"
          value={formData.confirmPassword}
          onChange={handleChange}
          disabled={fieldDisabled}
          error={errors.confirmPassword}
          autoComplete="new-password"
        />

        {isEditing ? (
          <div className="flex flex-wrap gap-3 pt-2">
            <Button type="submit" disabled={saving}>
              {saving ? "Updating…" : "Update password"}
            </Button>
            <Button type="button" variant="secondary" disabled={saving} onClick={handleCancelEdit}>
              Cancel
            </Button>
          </div>
        ) : null}
      </form>

      <p className="mt-6 text-sm text-text-500">
        <button
          type="button"
          className="font-semibold text-brand-900 hover:underline"
          onClick={() => setShowForgotPassword(true)}
        >
          Forgot your password?
        </button>
      </p>

      {showForgotPassword ? (
        <div className="mt-4">
          <ForgotPasswordResetPanel
            defaultEmail={user?.email ?? ""}
            onHide={() => setShowForgotPassword(false)}
          />
        </div>
      ) : null}
    </div>
  );
}
